/**
 * Queue module for managing failed conversion events
 * Implements localStorage-based queue with automatic retry
 */
import { log, warn, error as logError } from './logger.js';

const QUEUE_STORAGE_KEY = 'rbly_event_queue';
const TICK_INTERVAL_MS = 1000;  // scheduler tick
const BASE_DELAY_MS = 1000;     // 1s base for exponential backoff
const MAX_DELAY_MS = 60000;     // 60s cap
const MAX_RETRY_COUNT = 20;     // ~12 min total

let queueProcessorInterval = null;

/**
 * Get the current event queue from localStorage
 * @returns {Array} Array of queued events
 */
function getQueue() {
  try {
    const stored = localStorage.getItem(QUEUE_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    warn('[Rebrandly SDK] Error reading queue from localStorage:', error);
    return [];
  }
}

/**
 * Save the event queue to localStorage
 * @param {Array} queue - The queue to save
 * @returns {boolean} true if successful, false otherwise
 */
function saveQueue(queue) {
  try {
    localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue));
    return true;
  } catch (error) {
    warn('[Rebrandly SDK] Error saving queue to localStorage:', error);
    return false;
  }
}

/**
 * Generate a unique ID for queue entries
 * @returns {string} Unique ID
 */
function generateQueueId() {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Add an event to the queue
 * @param {string} endpoint - The API endpoint URL
 * @param {Object} payload - The event payload
 * @param {string} apiKey - The API key for authentication
 * @param {Object} [options] - Optional queue entry options
 * @param {string} [options.reason='network_error'] - Why the event was queued
 * @param {number} [options.maxRetries] - Max retry attempts (defaults to MAX_RETRY_COUNT)
 * @returns {Object} Result of enqueue operation
 */
function enqueueEvent(endpoint, payload, apiKey, options = {}) {
  try {
    const queue = getQueue();

    const queueEntry = {
      id: generateQueueId(),
      endpoint: endpoint,
      payload: payload,
      apiKey: apiKey,
      timestamp: payload.timestamp || new Date().toISOString(), // Preserve original timestamp
      retryCount: 0,
      lastAttempt: null,
      nextAttemptAt: new Date().toISOString(),
      enqueuedAt: new Date().toISOString(),
      reason: options.reason || 'network_error',
      maxRetries: options.maxRetries || MAX_RETRY_COUNT
    };

    queue.push(queueEntry);

    if (saveQueue(queue)) {
      log('[Rebrandly SDK] Event queued for retry:', payload.event);
      return {
        success: true,
        queued: true,
        queueId: queueEntry.id
      };
    }

    return {
      success: false,
      error: 'queue_storage_failed',
      message: 'Failed to save event to queue'
    };

  } catch (error) {
    logError('\[Rebrandly SDK] Error enqueuing event:', error);
    return {
      success: false,
      error: 'queue_error',
      message: error.message
    };
  }
}

/**
 * Remove an event from the queue by ID
 * @param {string} queueId - The queue entry ID to remove
 * @returns {boolean} true if removed, false otherwise
 */
function dequeueEvent(queueId) {
  try {
    const queue = getQueue();
    const filteredQueue = queue.filter(entry => entry.id !== queueId);

    if (filteredQueue.length < queue.length) {
      saveQueue(filteredQueue);
      log('[Rebrandly SDK] Event removed from queue:', queueId);
      return true;
    }

    return false;
  } catch (error) {
    warn('[Rebrandly SDK] Error dequeuing event:', error);
    return false;
  }
}

/**
 * Process the queue - attempt to send all queued events
 * This function is called automatically every RETRY_INTERVAL_MS
 * @param {Function} sendEventFn - The sendEvent function from api.js
 * @returns {Promise<Object>} Result of queue processing
 */
async function processQueue(sendEventFn) {
  const queue = getQueue();

  if (queue.length === 0) {
    return {
      success: true,
      processed: 0,
      succeeded: 0,
      failed: 0,
      removed: 0
    };
  }

  log(`[Rebrandly SDK] Processing queue: ${queue.length} event(s) pending`);

  let succeeded = 0;
  let failed = 0;
  let removed = 0;
  const updatedQueue = [];

  for (const entry of queue) {
    // Skip entries whose next attempt time hasn't arrived yet
    if (entry.nextAttemptAt && new Date(entry.nextAttemptAt) > new Date()) {
      updatedQueue.push(entry);
      continue;
    }

    // Check if max retries exceeded (per-entry or global default)
    const maxRetries = entry.maxRetries || MAX_RETRY_COUNT;
    if (entry.retryCount >= maxRetries) {
      warn(
        `[Rebrandly SDK] Max retries exceeded for event ${entry.payload.event}, removing from queue`
      );
      removed++;
      continue; // Don't add to updatedQueue (removes it)
    }

    // Update retry metadata
    entry.retryCount++;
    entry.lastAttempt = new Date().toISOString();

    try {
      // Attempt to send the event (without additional retries since we're already retrying)
      const result = await sendEventFn(entry.endpoint, entry.payload, entry.apiKey);

      if (result.success) {
        log(
          `[Rebrandly SDK] Queued event sent successfully: ${entry.payload.event}`
        );
        succeeded++;
        // Don't add to updatedQueue (removes it)
      } else {
        warn(
          `[Rebrandly SDK] Queued event failed (attempt ${entry.retryCount}): ${entry.payload.event}`
        );
        failed++;
        const delay = Math.min(BASE_DELAY_MS * Math.pow(2, entry.retryCount - 1), MAX_DELAY_MS);
        entry.nextAttemptAt = new Date(Date.now() + delay).toISOString();
        updatedQueue.push(entry); // Keep in queue for next retry
      }
    } catch (error) {
      warn(
        `[Rebrandly SDK] Error processing queued event: ${error.message}`
      );
      failed++;
      const delay = Math.min(BASE_DELAY_MS * Math.pow(2, entry.retryCount - 1), MAX_DELAY_MS);
      entry.nextAttemptAt = new Date(Date.now() + delay).toISOString();
      updatedQueue.push(entry); // Keep in queue for next retry
    }
  }

  // Save the updated queue
  saveQueue(updatedQueue);

  log(
    `[Rebrandly SDK] Queue processed: ${succeeded} succeeded, ${failed} failed, ${removed} removed`
  );

  return {
    success: true,
    processed: queue.length,
    succeeded,
    failed,
    removed,
    remaining: updatedQueue.length
  };
}

/**
 * Start the automatic queue processor
 * @param {Function} sendEventFn - The sendEvent function from api.js
 * @returns {boolean} true if started, false if already running
 */
function startQueueProcessor(sendEventFn) {
  if (queueProcessorInterval !== null) {
    warn('[Rebrandly SDK] Queue processor already running');
    return false;
  }

  log('[Rebrandly SDK] Starting queue processor (exponential backoff, 1s tick)');

  // Process immediately on start
  processQueue(sendEventFn).catch(error => {
    logError('\[Rebrandly SDK] Error in initial queue processing:', error);
  });

  // Then tick every TICK_INTERVAL_MS — actual retry rate is governed per-event by nextAttemptAt
  queueProcessorInterval = setInterval(() => {
    processQueue(sendEventFn).catch(error => {
      logError('\[Rebrandly SDK] Error in queue processing:', error);
    });
  }, TICK_INTERVAL_MS);

  return true;
}

/**
 * Stop the automatic queue processor
 * @returns {boolean} true if stopped, false if not running
 */
function stopQueueProcessor() {
  if (queueProcessorInterval === null) {
    warn('[Rebrandly SDK] Queue processor not running');
    return false;
  }

  clearInterval(queueProcessorInterval);
  queueProcessorInterval = null;
  log('[Rebrandly SDK] Queue processor stopped');
  return true;
}

/**
 * Clear all events from the queue
 * @returns {number} Number of events cleared
 */
function clearQueue() {
  try {
    const queue = getQueue();
    const count = queue.length;
    localStorage.removeItem(QUEUE_STORAGE_KEY);
    log(`[Rebrandly SDK] Queue cleared: ${count} event(s) removed`);
    return count;
  } catch (error) {
    warn('[Rebrandly SDK] Error clearing queue:', error);
    return 0;
  }
}

/**
 * Get queue statistics for debugging
 * @returns {Object} Queue statistics
 */
function getQueueStats() {
  try {
    const queue = getQueue();

    const stats = {
      totalEvents: queue.length,
      events: queue.map(entry => ({
        id: entry.id,
        event: entry.payload.event,
        retryCount: entry.retryCount,
        timestamp: entry.timestamp,
        enqueuedAt: entry.enqueuedAt,
        lastAttempt: entry.lastAttempt,
        nextAttemptAt: entry.nextAttemptAt
      })),
      oldestEvent: queue.length > 0 ? queue[0].enqueuedAt : null,
      processorRunning: queueProcessorInterval !== null
    };

    return stats;
  } catch (error) {
    warn('[Rebrandly SDK] Error getting queue stats:', error);
    return {
      totalEvents: 0,
      events: [],
      oldestEvent: null,
      processorRunning: false,
      error: error.message
    };
  }
}

export {
  enqueueEvent,
  dequeueEvent,
  getQueue,
  processQueue,
  startQueueProcessor,
  stopQueueProcessor,
  clearQueue,
  getQueueStats,
  QUEUE_STORAGE_KEY,
  TICK_INTERVAL_MS,
  BASE_DELAY_MS,
  MAX_DELAY_MS,
  MAX_RETRY_COUNT
};
